import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HrService } from '../../../services/human-resources/hr.service';
import { Employee } from '../../../models/hr-models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPenToSquare, faTrash, faPlus, faFileImport, faFileExport } from '@fortawesome/free-solid-svg-icons';
import { environment } from '@environments/environment';

// Extend the HrService interface to include Excel methods
declare module '../../../services/human-resources/hr.service' {
  interface HrService {
    importEmployeesFromExcel(file: File): Observable<any>;
    exportEmployeesToExcel(): Observable<Blob>;
  }
}

@Component({
  selector: 'app-employee-management',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, FontAwesomeModule],
  templateUrl: './employee-management.component.html',
  styleUrls: ['./employee-management.component.scss']
})
export class EmployeeManagementComponent implements OnInit {
  // Icon definitions
  editIcon = faPenToSquare;
  deleteIcon = faTrash;
  addIcon = faPlus;
  importIcon = faFileImport;
  exportIcon = faFileExport;

  employees$: Observable<Employee[]>;
  employeeForm: FormGroup;
  editingEmployee: Employee | null = null;
  showEmployeeModal = false;
  searchTerm = '';
  selectedDepartment = '';
  filteredEmployees$: Observable<Employee[]>;
  hasValidToken = false;
  departments: any[] = [];
  constructor(
    private hrService: HrService,
    private fb: FormBuilder,
    private router: Router
  ) {
    this.employees$ = this.hrService.employees$;
    this.filteredEmployees$ = this.employees$;
    this.employeeForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      department: [''],
      position: [''],
      hire_date: [null, Validators.required],
      birth_date: [null, Validators.required],
      workplace: [''],
      skills: ['']
    });
  }

  loadDepartments(): void {
    this.hrService.getDepartments().subscribe({
      next: (departments) => {
        console.log('[Department Management] Departments:', departments);
        this.departments = departments.filter(dept => dept.status === 1);
        console.log('[Employee Management] Active Departments:', this.departments);
      },
      error: (error) => {
        console.error('Error loading departments:', error);
      }
    });
  }

  ngOnInit(): void {
    // Check URL parameters for token and user info first
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    
    this.loadDepartments();
    if (urlToken) {
      console.log('EmployeeManagement: Found token in URL parameters, storing in localStorage');
      localStorage.setItem('auth_token', urlToken);
      
      // Store other user info if available
      const username = urlParams.get('username');
      const role = urlParams.get('role');
      const userId = urlParams.get('userId');
      const companyId = urlParams.get('companyId');
      
      if (username) localStorage.setItem('username', username);
      if (role) localStorage.setItem('userRole', role);
      if (userId) localStorage.setItem('userId', userId);
      if (companyId) localStorage.setItem('companyId', companyId);
      
      // Remove parameters from URL to avoid security risks
      const urlWithoutParams = window.location.pathname;
      window.history.replaceState({}, document.title, urlWithoutParams);
      
      console.log('EmployeeManagement: URL parameters processed and removed for security');
    }
  
    // Now check if we have a valid token
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.warn('EmployeeManagement: No auth token found. Redirecting to login...');
      alert('Your session has expired or you are not logged in. Please log in again.');
      this.router.navigate(['/hr/login']);
      return;
    }
    
    this.hasValidToken = true;
    console.log('EmployeeManagement: Auth token found. Loading employees...');
    
    this.hrService.refreshData();
    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredEmployees$ = this.employees$.pipe(
      map(employees => {
        let filtered = employees;
        
        // Filter by department
        if (this.selectedDepartment) {
          filtered = filtered.filter(emp => 
            emp.department?.toLowerCase() === this.selectedDepartment.toLowerCase()
          );
        }
        
        // Filter by search term
        if (this.searchTerm.trim()) {
          const searchLower = this.searchTerm.toLowerCase().trim();
          filtered = filtered.filter(emp =>
            emp.name.toLowerCase().includes(searchLower) ||
            emp.email.toLowerCase().includes(searchLower) ||
            emp.position?.toLowerCase().includes(searchLower) ||
            emp.department?.toLowerCase().includes(searchLower) ||
            emp.phone?.toLowerCase().includes(searchLower) ||
            emp.workplace?.toLowerCase().includes(searchLower) ||
            emp.skills?.toLowerCase().includes(searchLower)
          );
        }
        
        return filtered;
      })
    );
  }

  openAddEmployeeModal(): void {
    this.editingEmployee = null;
    this.employeeForm.reset();
    this.showEmployeeModal = true;
  }

  editEmployee(employee: Employee): void {
    // Create a deep copy to avoid mutations
    this.editingEmployee = JSON.parse(JSON.stringify(employee));
    console.log('Edit mode activated for employee ID:', employee.id);
    console.log('Employee to edit:', this.editingEmployee);
    
    // Format dates for the form with proper error handling
    let formattedHireDate = '';
    let formattedBirthDate = '';
    
    try {
      // Handle hire date with careful error checking
      if (employee.hireDate) {
        const hireDateObj = new Date(employee.hireDate);
        if (!isNaN(hireDateObj.getTime())) {
          formattedHireDate = hireDateObj.toISOString().split('T')[0];
        }
      }
      
      // Handle birth date with careful error checking
      if (employee.birthDate) {
        const birthDateObj = new Date(employee.birthDate);
        if (!isNaN(birthDateObj.getTime())) {
          formattedBirthDate = birthDateObj.toISOString().split('T')[0];
        }
      }
    } catch (error) {
      console.error('Error formatting dates:', error);
      // If date parsing fails, try an alternative approach or set empty values
      formattedHireDate = '';
      formattedBirthDate = '';
    }
    

    
    // Use patchValue instead of setValue to handle potential missing properties
    this.employeeForm.patchValue({
      name: employee.name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      department: employee.department || '',
      position: employee.position || '',
      hire_date: formattedHireDate,
      birth_date: formattedBirthDate,
      workplace: employee.workplace || '',
      skills: employee.skills || ''
    });
    
    // Show the modal
    this.showEmployeeModal = true;
  }

  closeEmployeeModal(): void {
    this.showEmployeeModal = false;
    this.employeeForm.reset();
  }

  saveEmployee(): void {
    // Double check token before submission
    const token = localStorage.getItem('auth_token');
    
    if (!token || !this.hasValidToken) {
      console.error('EmployeeManagement: No valid auth token found when trying to save employee');
      alert('Your session has expired or you are not logged in. Please log in again.');
      this.router.navigate(['/hr/login']);
      return;
    }
    
    console.log('EmployeeManagement: Auth token verified before submission');
    
    if (this.employeeForm.invalid) {
      // Mark all fields as touched to trigger validation messages
      Object.keys(this.employeeForm.controls).forEach(key => {
        const control = this.employeeForm.get(key);
        control?.markAsTouched();
      });
      
      // Add logging for debugging
      console.log('Form is invalid. Validation errors:', 
        Object.keys(this.employeeForm.controls)
          .filter(key => this.employeeForm.get(key)?.invalid)
          .map(key => `${key}: ${this.employeeForm.get(key)?.errors}`));
      
      return;
    }

    const formValue = this.employeeForm.value;
    console.log('Form values to be submitted:', formValue);

    // Ensure hire_date and birth_date are valid date strings in ISO format
    let hireDate: Date | null = null;
    let birthDate: Date | null = null;
    
    try {
      // Only create Date objects if values exist
      if (formValue.hire_date) {
        hireDate = new Date(formValue.hire_date);
        
        // Check if the date is valid
        if (isNaN(hireDate.getTime())) {
          throw new Error('Invalid hire date');
        }
      }
      
      if (formValue.birth_date) {
        birthDate = new Date(formValue.birth_date);
        
        // Check if the date is valid
        if (isNaN(birthDate.getTime())) {
          throw new Error('Invalid birth date');
        }
      }
    } catch (error) {
      console.error('Error parsing dates:', error);
      alert('Please enter valid dates for hire date and birth date');
      return;
    }

    const employee: Employee = {
      // If editing, use the existing ID, otherwise use 0 for a new employee
      id: this.editingEmployee ? this.editingEmployee.id : 0,
      name: formValue.name,
      email: formValue.email,
      phone: formValue.phone || null,
      department: formValue.department || null,
      position: formValue.position || null,
      hireDate: hireDate as Date, // Cast to satisfy type checker
      birthDate: birthDate as Date, // Cast to satisfy type checker
      workplace: formValue.workplace || null,
      skills: formValue.skills || null
    };

    console.log('Employee object to save:', employee);
    console.log('Is this an edit operation?', !!this.editingEmployee);
    console.log('Employee ID:', employee.id);

    if (this.editingEmployee) {
      // Add debugging logs
      console.log('Updating employee with ID:', this.editingEmployee.id);
      console.log('Employee data being sent to API:', JSON.stringify(employee));
      
      // Call the service to update the employee
      this.hrService.updateEmployee(employee).subscribe({
        next: (result) => {
          console.log('Successfully updated employee:', result);
          
          // Refresh the data to get the updated list
          this.hrService.refreshData();
          
          // Close the modal
          this.closeEmployeeModal();
          
          // Show success message
          alert('Employee updated successfully');
        },
        error: (error) => {
          console.error('Error updating employee:', error);
          console.error('Error details:', error.error || error.message);
          console.error('Error status:', error.status);
          
          if (error.status === 401 || error.status === 403) {
            alert('Authentication error: Your session may have expired. Please log in again.');
            this.router.navigate(['/hr/login']);
            return;
          }
          
          const errorMsg = error.error?.message || error.message || 'Unknown error occurred';
          alert(`Failed to update employee: ${errorMsg}`);
        }
      });
    } else {
      console.log('Adding new employee, sending to API:', employee);
      this.hrService.addEmployee(employee).subscribe({
        next: (result) => {
          console.log('Successfully added employee:', result);
          this.hrService.refreshData();
          this.closeEmployeeModal();
        },
        error: (error) => {
          console.error('Error adding employee:', error);
          if (error.status === 401 || error.status === 403) {
            alert('Authentication error: Your session may have expired. Please log in again.');
            this.router.navigate(['/hr/login']);
            return;
          }
          const errorMsg = error.error?.message || error.message || 'Unknown error occurred';
          alert(`Failed to add employee: ${errorMsg}`);
        }
      });
    }
  }

  deleteEmployee(id: number): void {
    if (confirm('Are you sure you want to delete this employee? This will also delete all related records.')) {
      this.hrService.deleteEmployee(id).subscribe({
        next: () => {
          console.log('Successfully deleted employee with ID:', id);
          if (this.editingEmployee && this.editingEmployee.id === id) {
            this.closeEmployeeModal();
          }
          this.hrService.refreshData(); // Refresh the data after deletion
        },
        error: (error) => {
          console.error('Error deleting employee:', error);
          if (error.status === 401 || error.status === 403) {
            alert('Authentication error: Your session may have expired. Please log in again.');
            this.router.navigate(['/hr/login']);
            return;
          }
          const errorMsg = error.error?.message || error.message || 'Unknown error occurred';
          alert(`Failed to delete employee: ${errorMsg}`);
        }
      });
    }
  }

  // Format date for display
  formatDate(date: Date | string | null): string {
    if (!date) {
      return 'N/A';
    }
    
    try {
      const dateObj = new Date(date);
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Invalid Date';
      }
      return dateObj.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date for display:', error);
      return 'Invalid Date';
    }
  }

  // Calculate age from birth date
  calculateAge(birthDate: Date | string | null): number | string {
    if (!birthDate) {
      return 'N/A';
    }
    
    try {
      const today = new Date();
      const birth = new Date(birthDate);
      
      // Check if date is valid
      if (isNaN(birth.getTime())) {
        return 'N/A';
      }
      
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      
      return age;
    } catch (error) {
      console.error('Error calculating age:', error);
      return 'N/A';
    }
  }

  // Calculate tenure (years of employment)
  calculateTenure(hireDate: Date | string | null): number | string {
    if (!hireDate) {
      return 'N/A';
    }
    
    try {
      const today = new Date();
      const hire = new Date(hireDate);
      
      // Check if date is valid
      if (isNaN(hire.getTime())) {
        return 'N/A';
      }
      
      let years = today.getFullYear() - hire.getFullYear();
      const monthDiff = today.getMonth() - hire.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < hire.getDate())) {
        years--;
      }
      
      return years;
    } catch (error) {
      console.error('Error calculating tenure:', error);
      return 'N/A';
    }
  }

  // Add new methods for Excel operations
  importExcel(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.hrService.importEmployeesFromExcel(file).subscribe({
        next: (response: { imported: number; errors: any[] }) => {
          console.log('Import successful:', response);
          alert(`Successfully imported ${response.imported} employees. ${response.errors.length ? `\nErrors: ${response.errors.length}` : ''}`);
          // Refresh the employee list
          this.hrService.refreshData();
        },
        error: (error: Error) => {
          console.error('Import failed:', error);
          alert('Failed to import employees. Please check the file format and try again.');
        }
      });
    }
  }

  exportExcel(): void {
    this.hrService.exportEmployeesToExcel().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `employees_${new Date().toISOString().split('T')[0]}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error: Error) => {
        console.error('Export failed:', error);
        alert('Failed to export employees. Please try again.');
      }
    });
  }

  getDepartmentName(departmentId: any): any {
    const department = this.departments.find(d => d.id === departmentId);
    return department ? department.name : 'N/A';
  }
} 